import React, { useEffect, useRef, useState } from 'react';

import { queryClient, QUERY_KEYS } from '../query';
import { ApiTypes } from 'common';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { style } from 'typestyle';
import { OverlayContainer } from '@src/components/OverlayContainer';
import { CloseButton } from '@src/components/CloseButton';
import { ActionButton } from '@src/components/ActionButton';
import { ModalCloseButtonPosition } from '@src/components/Modals/CloseButtonPosition';
import { FormTitle } from '@src/components/Form/FormTitle';
import { FormLineSeparator } from '@src/components/Form/FormLineSeparator';
import { Loader } from '@src/components/Loader';
import Alert from '@mui/material/Alert';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { API_URL } from '../config';
import { DownloadHelper } from '@src/services/helpers/download';

interface IProps {
  collectionId: string
  hide: () => void
}

export function DownloadPrompt({ collectionId, hide }: IProps) {
  const [isPreparing, setIsPreparing] = useState(false);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);

  const pollTimerRef = useRef<number | undefined>(undefined);
  const pollStartedAtRef = useRef<number | undefined>(undefined);
  const consecutiveErrorsRef = useRef(0);
  const downloadTriggeredRef = useRef(false);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  function stopPolling() {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = undefined;
    }
    pollStartedAtRef.current = undefined;
    setIsPreparing(false);
    setJobId(undefined);
    setDownloadUrl(undefined);
  }

  async function checkJob(jobIdToCheck: string) {
    if (downloadTriggeredRef.current) {
      return;
    }
    const maxWaitMs = 1000 * 60 * 10;
    const maxConsecutiveErrors = 5;
    if (pollStartedAtRef.current && Date.now() - pollStartedAtRef.current > maxWaitMs) {
      stopPolling();
      appHelpers.addAlert({ text: 'Download timed out. Please try again.', type: 'danger' });
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/share/ugcs/download/status/${jobIdToCheck}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 404 || response.status >= 500) {
          consecutiveErrorsRef.current += 1;
          if (consecutiveErrorsRef.current >= maxConsecutiveErrors) {
            stopPolling();
            appHelpers.addAlert({ text: 'Download failed. Please try again.', type: 'danger' });
          }
          return;
        }
        throw new Error(await response.text());
      }
      const payload = await response.json();
      consecutiveErrorsRef.current = 0;
      if (payload.status === 'done' && payload.url) {
        downloadTriggeredRef.current = true;
        stopPolling();
        setDownloadUrl(payload.url);
        DownloadHelper.triggerDownload(payload.url);
      } else if (payload.status === 'error') {
        stopPolling();
        appHelpers.addAlert({ text: payload.error || 'Download failed', type: 'danger' });
      }
    } catch (err: any) {
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= maxConsecutiveErrors) {
        stopPolling();
        appHelpers.addAlert({ text: err?.message || 'Download failed', type: 'danger' });
      }
    }
  }

  async function download() {
    const user = queryClient.getQueryData<ApiTypes.ResGetMe>(QUERY_KEYS.userConfig())?.user;
    const disable = user?.role === 'demo';
    if (disable) {
      appHelpers.addAlert({ text: 'common.error.role', type: 'danger' });
    } else {
      const whatToDownload = 'agreed';
      if (isPreparing) {
        return;
      }
      setIsPreparing(true);
      setJobId(undefined);
      setDownloadUrl(undefined);
      downloadTriggeredRef.current = false;
      try {
        const response = await fetch(`${API_URL}/api/share/ugcs/download/${collectionId}/${whatToDownload}`, {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!payload.jobId) {
          throw new Error('Missing download job id');
        }
        pollStartedAtRef.current = Date.now();
        consecutiveErrorsRef.current = 0;
        setJobId(payload.jobId);
        pollTimerRef.current = window.setInterval(() => checkJob(payload.jobId), 5000);
      } catch (err: any) {
        setIsPreparing(false);
        setDownloadUrl(undefined);
        appHelpers.addAlert({ text: err?.message || 'Download failed', type: 'danger' });
      }
    }
  }

  async function cancelJob() {
    if (!jobId) {
      stopPolling();
      return;
    }
    try {
      await fetch(`${API_URL}/api/share/ugcs/download/cancel/${jobId}`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Silent: cancellation is best-effort
    } finally {
      stopPolling();
    }
  }

  const agreed = true;
  return (
    <OverlayContainer
      containerClasses={[]}
      frameClasses={[overlayFrameClass]}
      onClose={() => hide()}
    >
      <div className={contentClass}>
        <ModalCloseButtonPosition>
          <CloseButton onClick={() => hide()} />
        </ModalCloseButtonPosition>
        <FormTitle value={window.T('create.collections.downloadchoice.prompt')} />
        <FormLineSeparator />
        <Alert
          className={videoWarningClass}
          variant="filled"
          severity="warning"
        >
          {lang.videoWarning}
        </Alert>
        <div className={buttonsClass}>
          <ActionButton
            variant="blue"
            disabled={!agreed || isPreparing}
            onAction={() => download()}
            style={{ width: '100%' }}
          >
            {window.T('create.collections.downloadchoice.rightapproved').toUpperCase()}
          </ActionButton>
        </div>
        {isPreparing ? (
          <div className={generatingClass}>
            <Loader size={20} />
            <div>Generating... This can take a few minutes for large collections.</div>
            <div className={generatingNoteClass}>Please keep this window open until the download starts.</div>
            <ActionButton
              variant="gray"
              size="small"
              onAction={() => cancelJob()}
            >
              Cancel
            </ActionButton>
          </div>
        ) : null}
        {downloadUrl ? (
          <div className={downloadLinkClass}>
            Download not starting?{' '}
            <a href={downloadUrl} target="_blank" rel="noreferrer">
              Click here to download
            </a>
            .
          </div>
        ) : null}
      </div>
    </OverlayContainer>
  );
}

const lang = LocalizedStrings({
  en: {
    videoWarning: 'Due to instagram limitations, video coming from this network shall not be available.',
  },
});

const overlayFrameClass = style({
  width: 520,
  position: 'relative',
  padding: 0,
});

const contentClass = style({
  padding: '32px 40px',
  position: 'relative',
});

const videoWarningClass = style({
  marginBottom: 20,
});

const buttonsClass = style({
  marginTop: 20,
  marginBottom: 8,
});

const generatingClass = style({
  marginTop: 16,
  fontSize: 13,
  color: '#c2c2c2',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
});

const generatingNoteClass = style({
  fontSize: 12,
});

const downloadLinkClass = style({
  marginTop: 12,
  fontSize: 13,
  color: '#c2c2c2',
  $nest: {
    a: {
      color: '#4da3ff',
      textDecoration: 'underline',
    },
    'a:hover': {
      color: '#7bbcff',
    },
  },
});
